/**
 * Public site pricing: agent HR layer (same pipeline as agent UI) → then public site commission.
 * Public never applies site commission on raw supplier / LDS engine amounts alone.
 */

import { computeAgentHotelFlowPriceVm } from "./agentHotelSearchResultsPricing";
import { hrDebugTrace, isHrDebugPricing } from "./hrDebugFlags";

/**
 * @param {object|null|undefined} agentHotelPricingLike
 * @returns {{ hotelHrCommissionPercentage: number|null, fixedHotelCommission: number }}
 */
export function normalizePublicAgentLayerInput(agentHotelPricingLike) {
  if (!agentHotelPricingLike || typeof agentHotelPricingLike !== "object") {
    return { hotelHrCommissionPercentage: null, fixedHotelCommission: 0 };
  }
  const hrRaw = agentHotelPricingLike.hotelHrCommissionPercentage;
  const hrNum = Number(hrRaw);
  return {
    hotelHrCommissionPercentage: Number.isFinite(hrNum) ? hrNum : null,
    fixedHotelCommission: Number(agentHotelPricingLike.fixedHotelCommission) || 0,
  };
}

/** חובה מספר סופי (כולל 0%) — בלי זה אין תמחור ציבור תקין */
export function isValidPublicAgentHotelPricing(agentHotelPricingLike) {
  const { hotelHrCommissionPercentage } = normalizePublicAgentLayerInput(agentHotelPricingLike);
  return hotelHrCommissionPercentage != null && Number.isFinite(hotelHrCommissionPercentage);
}

function logPublicMissingAgentPricing(payload) {
  try {
    hrDebugTrace("[PUBLIC_MISSING_AGENT_PRICING]", payload);
  } catch {
    /* ignore */
  }
  try {
    if (typeof console !== "undefined" && console.error) {
      console.error("[PUBLIC_MISSING_AGENT_PRICING]", payload);
    }
  } catch {
    /* ignore */
  }
}

export function logPublicPriceBaseTrace(payload) {
  if (isHrDebugPricing()) {
    hrDebugTrace("[PUBLIC_PRICE_BASE_TRACE]", payload);
  }
}

/**
 * @param {object} p
 * @param {number|null|undefined} p.convertedAmount — amount in displayCurrency after FX (agent pipeline input)
 * @param {string|null|undefined} p.displayCurrency — ISO currency of convertedAmount
 * @param {object|null|undefined} p.agentHotelPricing — { hotelHrCommissionPercentage, fixedHotelCommission }
 * @returns {{ agentFinalPrice: number|null, agentVm: object|null, hotelHrCommissionPercentage: number|null, fixedHotelCommission: number, pricingBlocked?: boolean }}
 */
export function applyAgentHotelLayerAfterConversion({
  convertedAmount,
  displayCurrency,
  agentHotelPricing = null,
  sourceFunction = "applyAgentHotelLayerAfterConversion",
  packageId = null,
  hotelId = null,
  roomId = null,
  rawSupplierAmount = null,
  suppressTrace = false,
}) {
  const { hotelHrCommissionPercentage, fixedHotelCommission } =
    normalizePublicAgentLayerInput(agentHotelPricing);
  const conv = Number(convertedAmount);
  const cur = String(displayCurrency || "").trim().toUpperCase();
  if (!Number.isFinite(conv) || conv <= 0 || !cur) {
    return {
      agentFinalPrice: Number.isFinite(conv) && conv > 0 ? conv : null,
      agentVm: null,
      hotelHrCommissionPercentage,
      fixedHotelCommission,
    };
  }

  if (!isValidPublicAgentHotelPricing(agentHotelPricing)) {
    logPublicMissingAgentPricing({
      sourceFunction,
      packageId,
      hotelId,
      roomId,
      convertedAmount: conv,
      displayCurrency: cur,
      hotelHrCommissionPercentage,
    });
    if (!suppressTrace) {
      logPublicPriceBaseTrace({
        rawSupplierAmount,
        convertedAmount: conv,
        agentHrCommissionPercent: hotelHrCommissionPercentage,
        fixedHotelCommission,
        agentFinalPrice: null,
        publicCommissionType: null,
        publicCommissionValue: null,
        publicFinalPrice: null,
        sourceFunction,
        packageId,
        hotelId,
        roomId,
        note: "missing_agent_hotel_pricing_block",
      });
    }
    return {
      agentFinalPrice: null,
      agentVm: null,
      hotelHrCommissionPercentage,
      fixedHotelCommission,
      pricingBlocked: true,
    };
  }

  const agentVm = computeAgentHotelFlowPriceVm({
    apiPrice: conv,
    transactionCurrency: cur,
    hotelSearchResultsCurrency: cur,
    hotelSearchListDisplayCurrency: cur,
    hotelHrCommissionPercentage,
    fixedHotelCommission,
  });

  const agentFinalPrice =
    agentVm?.pricingStatus === "ok" && agentVm.finalPrice != null ? agentVm.finalPrice : conv;
  if (!suppressTrace) {
    logPublicPriceBaseTrace({
      rawSupplierAmount,
      convertedAmount: conv,
      agentHrCommissionPercent: hotelHrCommissionPercentage,
      fixedHotelCommission,
      agentFinalPrice,
      publicCommissionType: null,
      publicCommissionValue: null,
      publicFinalPrice: null,
      sourceFunction,
      packageId,
      hotelId,
      roomId,
    });
  }

  return {
    agentFinalPrice,
    agentVm,
    hotelHrCommissionPercentage,
    fixedHotelCommission,
  };
}

/**
 * ILS-only path (e.g. LDS engine totals): same HR pipeline in ILS.
 * @param {object} p
 * @param {number|null|undefined} p.ilsAmount
 * @returns {{ agentFinalPrice: number|null, agentVm: object|null, hotelHrCommissionPercentage: number|null, fixedHotelCommission: number }}
 */
export function applyAgentHotelLayerToIlsAmount({
  ilsAmount,
  agentHotelPricing = null,
  sourceFunction = "applyAgentHotelLayerToIlsAmount",
  packageId = null,
  hotelId = null,
  roomId = null,
  rawSupplierAmount = null,
  suppressTrace = false,
}) {
  return applyAgentHotelLayerAfterConversion({
    convertedAmount: ilsAmount,
    displayCurrency: "ILS",
    agentHotelPricing,
    sourceFunction,
    packageId,
    hotelId,
    roomId,
    rawSupplierAmount,
    suppressTrace,
  });
}

/**
 * @param {object} p
 * @param {import("./publicSitePricingCommission").CommissionNode|object} p.publicCommission
 * @param {number|null|undefined} p.publicFinalPrice
 */
export function logPublicPriceBaseTraceAfterPublicCommission({
  rawSupplierAmount,
  convertedAmount,
  agentHrCommissionPercent,
  fixedHotelCommission,
  agentFinalPrice,
  publicCommission,
  publicFinalPrice,
  sourceFunction,
  packageId,
  hotelId,
  roomId,
}) {
  const pc = publicCommission && typeof publicCommission === "object" ? publicCommission : {};
  logPublicPriceBaseTrace({
    rawSupplierAmount,
    convertedAmount,
    agentHrCommissionPercent,
    fixedHotelCommission,
    agentFinalPrice,
    publicCommissionType: pc.type ?? null,
    publicCommissionValue: pc.value != null ? Number(pc.value) : null,
    publicFinalPrice,
    sourceFunction,
    packageId,
    hotelId,
    roomId,
  });
}
